import { FC } from 'react';
import { Alert, Box, Chip, Link, Skeleton, Svg, Text } from '@hopskipdrive/react-components';
import EmailIcon from '@mui/icons-material/Email';
import PhoneEnabledIcon from '@mui/icons-material/PhoneEnabled';
import { capitalize } from 'lodash';

import { CopyToClipboard } from '../../../shared/components/CopyToClipboard';
import { PhotoOrInitials } from '../../../shared/components/PhotoOrInitials';
import { RiderInfoContainer } from '../../../shared/components/RiderInfoContainer';
import { colorPalette, colorTokens } from '../../../shared/components/variables';
import { useFlags } from '../../../shared/contexts/LDContext';
import { ErrorBoundary } from '../../../shared/utils/errorReporting/ErrorBoundary';
import { Heading } from '../../features/InfoNav/RiderDetailsTab/RiderDetails/RiderDetails.styles';
import { getSafetyBadgeProps } from '../../features/InfoNav/RiderDetailsTab/RiderDetails/utils';
import {
  RiderRequiredServices,
  RiderRequiredServicesSkeleton,
} from '../../features/InfoNav/RiderDetailsTab/RiderRequiredServices';
import { replaceUnderscoreWithSpacePascalCase } from '../../features/PageHeading/utils';
import { RiderDetails } from '../../types/rider';

import { MustBeMetCard, MustBeMetCardSkeleton } from './MustBeMetCard';

interface IRiderDetailsPresenterProps {
  riderSlug: string;
  profileUrl: string;
  isRemovedRider?: boolean;
  riderDetails: RiderDetails;
  isFetching: boolean;
  isCarePartnerRide?: boolean;
}

export const RiderDetailsPresenter = ({
  riderSlug,
  profileUrl,
  isRemovedRider,
  riderDetails,
  isFetching,
  isCarePartnerRide = false,
}: IRiderDetailsPresenterProps) => {
  const { enableOssCarePartnerRideDetails } = useFlags();

  return (
    <Heading>
      {isRemovedRider && (
        <Alert
          variant="filled"
          severity="success"
          message="This Rider has been removed from the ride"
          style={{ backgroundColor: colorPalette.neutral350, fontSize: '20px' }}
        />
      )}
      {riderDetails?.phoneNumberWarnings &&
        riderDetails?.phoneNumberWarnings?.map((phoneError) => (
          <Alert
            key={phoneError}
            variant="outlined"
            severity="warning"
            message={`Error: ${phoneError}`}
            action={
              <Link
                data-testid="phone-error-link"
                target="_blank"
                href={profileUrl}
                style={{
                  textDecoration: 'none',
                }}
              >
                <Text
                  variant="button"
                  style={{
                    color: colorTokens.warning.dark,
                  }}
                >
                  See in Admin
                </Text>
              </Link>
            }
            style={{ fontSize: '20px' }}
          />
        ))}

      <Box display="flex" flexDirection="row">
        <Box display="flex" flexDirection="column" minWidth="310px" gap="12px">
          <Box>
            {isFetching ? (
              <>
                <Skeleton data-testid="main-heading-skeleton" variant="circular" width={92} height={92} />
                <Skeleton variant="text" width={135} height={32} />
                <Skeleton variant="text" width={130} height={24} />
              </>
            ) : (
              <>
                <PhotoOrInitials
                  pictureUrl={riderDetails?.profilePhotoUrl || ''}
                  key={riderDetails?.slug}
                  fullName={`${riderDetails?.firstName} ${riderDetails?.lastName}`}
                />

                <Link
                  target="_blank"
                  variant="h5"
                  underline="hover"
                  href={profileUrl}
                  style={{ color: colorTokens.text.primary, fontWeight: 500 }}
                >
                  {isRemovedRider
                    ? `${riderDetails?.firstName} ${riderDetails?.lastName} (Removed)`
                    : `${riderDetails?.firstName} ${riderDetails?.lastName}`}
                </Link>
                <Text variant="body1">{`${riderDetails?.age}y/o (${riderDetails?.pronouns})`}</Text>
              </>
            )}
          </Box>
          <Box>
            <RiderInfoContainer isFetching={isFetching}>
              {riderDetails?.mobilePhoneNumber && (
                <>
                  <PhoneEnabledIcon style={{ color: colorTokens.text.secondary }} />
                  <CopyToClipboard
                    toastMessage="Phone Number Copied"
                    text={riderDetails?.mobilePhoneNumber}
                    variant="h6"
                    color={colorTokens.text.secondary}
                  />
                </>
              )}
            </RiderInfoContainer>
            <RiderInfoContainer isFetching={isFetching}>
              {riderDetails?.email && (
                <>
                  <EmailIcon style={{ color: colorTokens.text.secondary }} />
                  <CopyToClipboard
                    toastMessage="Email Copied"
                    text={riderDetails?.email}
                    variant="h6"
                    color={colorTokens.text.secondary}
                    truncate={true}
                  />
                </>
              )}
            </RiderInfoContainer>
          </Box>
          <Box display="flex" flexDirection="row" alignItems="center" gap="5px">
            <Svg icon="directions-car-filled" style={{ color: colorTokens.text.secondary }} />
            {isFetching ? (
              <Skeleton width="189px" />
            ) : (
              <Text variant="caption" style={{ color: colorTokens.text.secondary }}>{`${
                riderDetails?.totalTripsCompleted
              } Trips ${riderDetails?.activatedOn ? `Activated ${riderDetails?.activatedOn}` : ''}`}</Text>
            )}
          </Box>
        </Box>
        <Box display="flex" flexDirection="column" minWidth="310px">
          <RiderAdditionalInfo isFetching={isFetching} label="Code Word">
            {riderDetails?.codeWord}
          </RiderAdditionalInfo>
          <RiderAdditionalInfo isFetching={isFetching} label="Driver Gender Preference">
            {riderDetails?.driverGenderPreferences?.join(', ')}
          </RiderAdditionalInfo>
          <RiderAdditionalInfo isFetching={isFetching} label="Gender">
            {capitalize(riderDetails?.gender)}
          </RiderAdditionalInfo>
          <RiderAdditionalInfo isFetching={isFetching} label="Category">
            {replaceUnderscoreWithSpacePascalCase(riderDetails?.category)}
          </RiderAdditionalInfo>
        </Box>
      </Box>
      {enableOssCarePartnerRideDetails && (
        <ErrorBoundary FallbackComponent={RiderRequiredServicesSkeleton}>
          <RiderRequiredServices riderSlug={riderSlug} isCarePartnerRide={isCarePartnerRide} />
        </ErrorBoundary>
      )}
      {(isFetching || !!riderDetails?.safetyDetails?.length) && (
        <Box display="flex" flexDirection="column" alignItems="flex-start" gap="6px" width="100%">
          <Text variant="subtitle1" style={{ color: colorTokens.text.secondary }}>
            Safety Settings
          </Text>
          <RiderSafetyBadges isFetching={isFetching} riderSlug={riderSlug} safetyBadges={riderDetails?.safetyDetails} />
        </Box>
      )}
    </Heading>
  );
};

interface RiderMainHeadingProps {
  orgName: string;
  slug: string;
  children?: React.ReactNode;
}

export const RiderMainHeading: FC<RiderMainHeadingProps> = ({ orgName, slug, children }) => {
  return (
    <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" gap="12px" width="100%">
      <Box display="flex" flexDirection="row" alignItems="center" gap="12px">
        <Svg icon="sidebar_riders" height="24px" style={{ color: colorTokens.primary.main, minWidth: '24px' }} />
        <Text variant="h6" style={{ color: colorTokens.primary.main }}>{`${orgName}’s Rider`}</Text>
        <CopyToClipboard toastMessage="Slug Copied" text={slug} variant="h6" color={colorTokens.text.secondary} />
      </Box>
      {children}
    </Box>
  );
};

export const RiderAdditionalInfo = ({ isFetching, label, children }) => {
  return (
    <Box borderBottom="1px solid #DFE4E8">
      <Text variant="subtitle1" style={{ color: colorTokens.text.secondary }}>
        {label}
      </Text>
      {isFetching ? (
        <Skeleton data-testid="rider-additional-skeleton-loader" variant="text" width={100} height={20} />
      ) : (
        <Text variant="h6">{children || '--'}</Text>
      )}
    </Box>
  );
};

interface RiderSafetyBadgesProps {
  isFetching: boolean;
  riderSlug: string;
  safetyBadges: string[];
}

export const RiderSafetyBadges: FC<RiderSafetyBadgesProps> = ({ isFetching, riderSlug, safetyBadges }) => {
  safetyBadges = safetyBadges && safetyBadges.filter((badge) => badge !== 'Must be met at drop-off');

  return (
    <Box display="flex" gap="8px" flexWrap="wrap" mb={6}>
      {isFetching ? (
        <>
          <Skeleton width={207} height={35} />
          <Skeleton width={143} height={35} />
          <Skeleton width={143} height={35} />
        </>
      ) : (
        <>
          {safetyBadges?.map((badge) => (
            <Chip key={badge} type="square" label={badge} {...getSafetyBadgeProps(badge)} />
          ))}
          <ErrorBoundary FallbackComponent={MustBeMetCardSkeleton}>
            <MustBeMetCard riderSlug={riderSlug} />
          </ErrorBoundary>
        </>
      )}
    </Box>
  );
};
